/**
 * BookingActions - Payment method selection and form submission
 *
 * i18n (Phase 5):
 *   - Uses booking.* (back, instantConfirmation, selectRideType,
 *     acceptTermsPrefix, cguLinkText, andText, refundPolicyLinkText,
 *     acceptTermsSuffix, mustAcceptTerms) and payment.* (phone, email, notes,
 *     paymentMethod, creditCard, securePayment, stripeDesc, pay,
 *     redirecting) namespaces.
 *   - Receives `t` as a prop (kept consistent with sibling components in the
 *     BookingWidget folder; see BookingWidget.js orchestrator).
 *   - "Black Matte" and the EUR symbol are intentionally hardcoded.
 *   - Stripe (card) is the only online payment method.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, CreditCard, Loader2, AlertCircle, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Link } from 'react-router-dom';

export const BookingActions = ({
  formData,
  setFormData,
  acceptedTerms,
  setAcceptedTerms,
  termsError,
  setTermsError,
  loading,
  rideOptions,
  selectedOption,
  estimate,
  onBack,
  onSubmit,
  isRTL,
  t
}) => {
  const handleSubmitClick = (e) => {
    if (!acceptedTerms) {
      e.preventDefault();
      setTermsError(true);
      return;
    }
    onSubmit(e);
  };

  const price = selectedOption?.estimated_price || estimate?.estimated_price || '';

  return (
    <div className="space-y-4">
      {/* Full name (B2C invoicing) — optional, non-blocking */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('payment.fullName', 'Nom complet')}
        </Label>
        <div className="relative">
          <Input
            type="text"
            value={formData.customer_name}
            onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
            placeholder={t('payment.fullNamePlaceholder', 'Nom et prénom')}
            className="bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 rounded-xl h-11"
            data-testid="customer-name-input"
          />
        </div>
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('payment.phone')}
        </Label>
        <div className="relative">
          <Phone className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400`} />
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder={t('payment.phonePlaceholder')}
            className={`${isRTL ? 'pr-10' : 'pl-10'} bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 rounded-xl h-11`}
            required
            data-testid="phone-input"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('payment.email')}
        </Label>
        <div className="relative">
          <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400`} />
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder={t('payment.emailPlaceholder')}
            className={`${isRTL ? 'pr-10' : 'pl-10'} bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 rounded-xl h-11`}
            required
            data-testid="email-input"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('payment.notes')}
        </Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder={t('payment.notesPlaceholder')}
          className="bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 min-h-[80px] rounded-xl"
          data-testid="notes-input"
        />
      </div>

      {/* Payment Method — card via Stripe (the only online payment method) */}
      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('payment.paymentMethod')}
        </Label>
        <div
          className="p-4 rounded-xl border-2 bg-black border-black text-white"
          data-testid="payment-method-stripe"
        >
          <CreditCard className="w-5 h-5 mx-auto mb-2" />
          <span className="block text-center text-sm font-medium">{t('payment.creditCard')}</span>
        </div>
      </div>

      {/* Secure Payment Info */}
      <div className="bg-neutral-50 border border-neutral-200 p-4 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-4 h-4 text-neutral-500" />
          <span className="text-xs uppercase tracking-wide text-black font-semibold">
            {t('payment.securePayment')}
          </span>
        </div>
        <p className="text-neutral-500 text-xs">
          {t('payment.stripeDesc')}
        </p>
      </div>

      {/* Terms Acceptance */}
      <div className="space-y-2">
        <div className="flex items-start gap-3">
          <Checkbox
            id="accept-terms"
            checked={acceptedTerms}
            onCheckedChange={(checked) => {
              setAcceptedTerms(checked);
              if (checked) setTermsError(false);
            }}
            className="mt-1 border-neutral-300 data-[state=checked]:bg-black data-[state=checked]:border-black"
            data-testid="accept-terms-checkbox"
          />
          <label htmlFor="accept-terms" className="text-sm text-neutral-600 leading-relaxed cursor-pointer">
            {t('booking.acceptTermsPrefix')}
            <Link to="/legal/cgu" target="_blank" className="text-black underline hover:no-underline font-medium">
              {t('booking.cguLinkText')}
            </Link>
            {t('booking.andText')}
            <Link to="/legal/refund-policy" target="_blank" className="text-black underline hover:no-underline font-medium">
              {t('booking.refundPolicyLinkText')}
            </Link>
            {t('booking.acceptTermsSuffix')}
          </label>
        </div>

        {termsError && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-xl"
            data-testid="terms-error-message"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{t('booking.mustAcceptTerms')}</span>
          </motion.div>
        )}
      </div>

      {/* Selection Reminder */}
      {rideOptions && !selectedOption && (
        <div className="bg-orange-50 border border-orange-200 p-3 rounded-xl text-center">
          <p className="text-orange-600 text-sm font-medium">{t('booking.selectRideType')}</p>
        </div>
      )}

      {/* Payment Buttons */}
      <div className="space-y-3">
        <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 rounded-xl"
            data-testid="back-to-step-1"
          >
            {t('booking.back')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmitClick}
            disabled={loading || !formData.phone || !formData.email || (rideOptions && !selectedOption)}
            className="flex-1 bg-black text-white hover:bg-neutral-800 py-6 font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 disabled:opacity-50"
            data-testid="submit-payment"
          >
            {loading ? (
              <>
                <Loader2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} animate-spin`} />
                {t('payment.redirecting')}
              </>
            ) : (
              <>
                <CreditCard className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('payment.pay')} {price} €
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Trust Indicator */}
      <div className="flex items-center justify-center gap-2 pt-2">
        <Zap className="w-4 h-4 text-yellow-500" />
        <span className="text-xs text-neutral-500">{t('booking.instantConfirmation')}</span>
      </div>
    </div>
  );
};

export default BookingActions;
